import { useOrganizationStore } from '../stores/useOrganizationStore';
import { createFlow as createFlowApi } from '../api/remiApi';
import { createCloudRunAxios } from '../api/axios';

// Webhook URL for Convert flow integration setup notifications
const CONVERT_INTEGRATION_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/12395623/ufvxfwd/';

const remiApi = createCloudRunAxios('remi-api');

const getOrganizationId = (): string => {
  const { organization } = useOrganizationStore.getState();
  if (!organization?.id) {
    throw new Error('No organization ID found');
  }
  return organization.id;
};

type FlowType = 'Revive' | 'Engage' | 'Convert';

interface BaseFlowSettings {
  name: string;
  description: string;
  description_id: string;
  flow_type: FlowType;
  sub_type: string;
  timezone: string;
  country_code: string;
  transfer_numbers: Record<string, string>;
  voicemail_content?: string;
}

interface ScheduledFlowSettings extends BaseFlowSettings {
  flow_type: 'Engage' | 'Revive';
  scheduled_for: string;
  scheduling_window: number;
  schedule_over_unit: string;
  allowed_days: string[];
  allowed_hours: {
    start_hour: number;
    end_hour: number;
  };
  exclude_holidays: boolean;
  useRoundRobin?: boolean;
}

// Integration Setup data for non-FUB users
interface IntegrationSetupData {
  contactType: 'buyer' | 'seller' | 'both' | '';
  crmSourceType: 'random' | 'specific' | '';
  crmSources: string[];
  useTags: boolean;
  tagsAction: 'include' | 'exclude' | '';
  tags: string[];
  useAssignment: boolean;
  assignmentAction: 'include' | 'exclude' | '';
  assignedAgents: string[];
  additionalNotes: string;
  status?: string;
  submittedBy?: string;
  submittedByName?: string;
  submittedAt?: string;
  organizationName?: string;
}

interface ConvertFlowSettings extends BaseFlowSettings {
  flow_type: 'Convert';
  lead_email: string;
  lead_source: string;
  useRoundRobin?: boolean;
  integrationSetup?: IntegrationSetupData;
}

type FlowSettings = ScheduledFlowSettings | ConvertFlowSettings;

interface CreateFlowOptions {
  skipNameAbbreviation?: boolean;
}

/**
 * Trigger webhook with integration setup data
 * This is fire-and-forget - webhook failure should not block the request
 */
const triggerConvertIntegrationWebhook = async (
  flowId: string,
  flowName: string,
  organizationId: string,
  integrationSetup: IntegrationSetupData
) => {
  const webhookPayload = {
    // Organization info
    orgId: organizationId,
    orgName: integrationSetup.organizationName || '',

    // Flow info
    flowId,
    flowName,

    // Integration setup data
    contactType: integrationSetup.contactType,
    crmSourceType: integrationSetup.crmSourceType,
    crmSources: integrationSetup.crmSources,
    useTags: integrationSetup.useTags,
    tagsAction: integrationSetup.tagsAction,
    tags: integrationSetup.tags,
    useAssignment: integrationSetup.useAssignment,
    assignmentAction: integrationSetup.assignmentAction,
    assignedAgents: integrationSetup.assignedAgents,
    additionalNotes: integrationSetup.additionalNotes || '',

    // Metadata
    submittedBy: integrationSetup.submittedBy || '',
    submittedByName: integrationSetup.submittedByName || '',
    submittedAt: integrationSetup.submittedAt || new Date().toISOString(),
  };

  try {
    await fetch(CONVERT_INTEGRATION_WEBHOOK_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(webhookPayload),
    });
    // Note: With no-cors mode, we can't read the response status
    // but the request will still be sent successfully
  } catch (err) {
    // Webhook failure should not block the request
    console.warn('Convert integration webhook notification failed:', err);
  }
};

/**
 * Save integration setup request to Remi API
 * All setup data goes in config_data as freeform object
 * Also triggers Zapier webhook notification
 */
export const saveIntegrationSetupRequest = async (
  flowId: string,
  flowName: string,
  organizationId: string,
  integrationSetup: IntegrationSetupData
) => {
  const response = await remiApi.post(
    `/api/v1/organizations/${organizationId}/integration-setup-requests`,
    {
      integration_type: 'crm',
      integration_name: flowName,
      config_data: {
        flowId,
        flowName,
        orgId: organizationId,
        orgName: integrationSetup.organizationName || '',
        contactType: integrationSetup.contactType,
        crmSourceType: integrationSetup.crmSourceType,
        crmSources: integrationSetup.crmSources,
        useTags: integrationSetup.useTags,
        tagsAction: integrationSetup.tagsAction,
        tags: integrationSetup.tags,
        useAssignment: integrationSetup.useAssignment,
        assignmentAction: integrationSetup.assignmentAction,
        assignedAgents: integrationSetup.assignedAgents,
        additionalNotes: integrationSetup.additionalNotes || '',
        submittedBy: integrationSetup.submittedBy || '',
        submittedByName: integrationSetup.submittedByName || '',
        submittedAt: integrationSetup.submittedAt || new Date().toISOString(),
        status: 'pending',
      },
    }
  );

  // Trigger webhook notification (fire-and-forget)
  triggerConvertIntegrationWebhook(flowId, flowName, organizationId, integrationSetup);

  return response.data?.id ?? response.data?.request_id ?? '';
};

export const createFlow = async (settings: FlowSettings, options?: CreateFlowOptions) => {
  const organizationId = getOrganizationId();

  try {
    // Trim name and description, optionally convert buyer/seller to B/S abbreviations
    let trimmedName = settings.name.trim();
    // Check if lead_source is speculo_seller or speculo_buyer - if so, skip abbreviation
    const leadSource = (settings as ConvertFlowSettings).lead_source?.toLowerCase() || '';
    const isSpeculoSource = leadSource === 'speculo_seller' || leadSource === 'speculo_buyer';
    if (!options?.skipNameAbbreviation && !isSpeculoSource) {
      trimmedName = trimmedName.replace(/\bbuyer\b/gi, 'B').replace(/\bseller\b/gi, 'S');
    }
    const trimmedDescription = settings.description.trim();

    // Build base flow payload for Remi API (snake_case)
    const basePayload: Record<string, unknown> = {
      name: trimmedName,
      description: trimmedDescription,
      description_id: settings.description_id,
      flow_type: settings.flow_type,
      sub_type: settings.sub_type,
      timezone: settings.timezone,
      country_code: settings.country_code,
      transfer_numbers: settings.transfer_numbers,
      status: settings.flow_type === 'Convert' ? 'active' : 'draft',
      rejoin: false,
      ...(settings.voicemail_content
        ? { voicemail_content: settings.voicemail_content }
        : {}),
    };

    // Build type-specific payload
    const convertSettings = settings as ConvertFlowSettings;
    const flowPayload =
      settings.flow_type === 'Convert'
        ? {
            ...basePayload,
            workflow_type: 'speed_to_lead',
            strategy_id: 'speed_to_lead',
            max_attempts: 12,
            cadence: 1,
            lead_email: convertSettings.lead_email,
            lead_source: convertSettings.lead_source,
            allowed_days: [
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
              'Sunday',
            ],
            allowed_hours: JSON.stringify({ start_hour: 8, end_hour: 20 }),
            exclude_holidays: false,
            use_round_robin: convertSettings.useRoundRobin || false,
          }
        : {
            ...basePayload,
            workflow_type: 'scheduled_call',
            max_attempts: settings.flow_type === 'Revive' ? 5 : 1,
            cadence: 1,
            scheduled_for: (settings as ScheduledFlowSettings).scheduled_for,
            scheduling_window: (settings as ScheduledFlowSettings).scheduling_window,
            schedule_over_unit: (settings as ScheduledFlowSettings).schedule_over_unit,
            allowed_days: JSON.stringify((settings as ScheduledFlowSettings).allowed_days),
            allowed_hours: JSON.stringify({
              start_hour: parseInt(
                (settings as ScheduledFlowSettings).allowed_hours.start_hour.toString()
              ),
              end_hour: parseInt(
                (settings as ScheduledFlowSettings).allowed_hours.end_hour.toString()
              ),
            }),
            exclude_holidays: (settings as ScheduledFlowSettings).exclude_holidays,
            use_round_robin: (settings as ScheduledFlowSettings).useRoundRobin || false,
          };

    // Create flow via Remi API (handles Scripts, Insights, Rules, KnowledgeBases side effects)
    const result = await createFlowApi(organizationId, flowPayload);
    const flowId = result?.flow_id ?? result?.flow?.id ?? result?.id;

    if (!flowId) {
      throw new Error('Flow creation succeeded but no flow_id returned');
    }

    // Save integration setup request for orgs with no existing integration (if provided)
    let integrationSetupFailed = false;
    if (settings.flow_type === 'Convert' && convertSettings.integrationSetup?.contactType) {
      try {
        await saveIntegrationSetupRequest(
          flowId,
          trimmedName,
          organizationId,
          convertSettings.integrationSetup
        );
      } catch (integrationError) {
        console.warn('Integration setup save failed (flow still created):', integrationError);
        integrationSetupFailed = true;
        // Don't throw - flow is still valid
      }
    }

    return {
      success: true,
      flowId,
      flowName: trimmedName,
      flowType: settings.flow_type,
      description: trimmedDescription,
      flowInfo: result?.flow ?? flowPayload,
      integrationSetupFailed,
    };
  } catch (error) {
    console.error('Error creating flow:', error);
    throw error;
  }
};
